import { i18n } from '@/i18n'
import { tenantBrandingString, type TenantBrandingRecord } from '@/services/api/tenant-branding'

const FALLBACK_TITLE = 'Ticket Buddy Admin'
const FALLBACK_DESCRIPTION = 'Manage events, tickets, orders and tenant settings in the Ticket Buddy admin panel.'

function clean(value: string): string {
  return value.replace(/\s+/g, ' ').trim()
}

export function adminPanelName(branding: TenantBrandingRecord): string {
  const tenantName = clean(
    tenantBrandingString(branding, 'shopName') ||
    tenantBrandingString(branding, 'appDisplayName') ||
    tenantBrandingString(branding, 'shortName'),
  )
  return tenantName ? `${tenantName} Admin` : FALLBACK_TITLE
}

export function adminSeoDescription(branding: TenantBrandingRecord): string {
  const tenantName = clean(
    tenantBrandingString(branding, 'shopName') ||
    tenantBrandingString(branding, 'appDisplayName') ||
    tenantBrandingString(branding, 'shortName'),
  )
  if (!tenantName) return FALLBACK_DESCRIPTION
  return `Manage events, tickets, orders and tenant settings for ${tenantName} in the Ticket Buddy admin panel.`
}

function ensureMeta(selector: string, attrs: Record<string, string>): HTMLMetaElement {
  let node = document.head.querySelector<HTMLMetaElement>(selector)
  if (!node) {
    node = document.createElement('meta')
    for (const [key, value] of Object.entries(attrs)) {
      if (key !== 'content') node.setAttribute(key, value)
    }
    document.head.appendChild(node)
  }
  return node
}

function setMeta(name: string, content: string): void {
  const node = ensureMeta(`meta[name="${name}"]`, { name })
  node.setAttribute('content', content)
}

function setPropertyMeta(property: string, content: string): void {
  const node = ensureMeta(`meta[property="${property}"]`, { property })
  node.setAttribute('content', content)
}

export function applyAdminSeoMeta(branding: TenantBrandingRecord, title: string): void {
  const description = adminSeoDescription(branding)
  setMeta('description', description)
  setPropertyMeta('og:title', title)
  setPropertyMeta('og:description', description)
  setMeta('twitter:title', title)
  setMeta('twitter:description', description)
}

export function buildAdminDocumentTitle(branding: TenantBrandingRecord, pageTitle = ''): string {
  const suffix = adminPanelName(branding)
  const page = clean(pageTitle)
  return page ? `${page} · ${suffix}` : suffix
}

export function fallbackAdminTitle(): string {
  return String(i18n.global.t('app.name') || FALLBACK_TITLE)
}
